import Vue from 'vue';
import {
  DOMAIN_PROPS_KEYS,
  DOMAIN_PROPS_ACCESS_TYPES
} from '@/common/constants/domainProps.constants';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import useAuthStore from '@/common/store/pinia/auth';

interface DomainPropsState {
  [key: string]: any,
}

function getInitialState(): DomainPropsState {
  return {
    [DOMAIN_PROPS_KEYS.ALLOW_LIVE_RESIZE]: { enabled: false },
    [DOMAIN_PROPS_KEYS.ALLOW_LIVE_RESIZE_MIGRATION]: { enabled: false },
    [DOMAIN_PROPS_KEYS.ALLOW_COMPUTE_BACKUP]: { enabled: true },
  };
}

const domainPropsGetters: CommonGetterTree<DomainPropsState> = {
  getCurrentDomainProp: (_state, getters) => key => {
    const authStore = useAuthStore();
    return getters.getDomainProp(authStore.currentDomain)(key);
  },
  getDomainProp: state => domain => key => (Object.hasOwn(state.data, domain) ? state.data[domain] : getInitialState())[key]
};

const domainPropsMutations: CommonMutationTree<DomainPropsState> = {
  SET(state, { domain, key, value }) {
    if (!state.data[domain]) {
      Vue.set(state.data, domain, getInitialState());
    }
    Vue.set(state.data[domain], key, value);
  },
  DELETE(state, { domain, key }) {
    Vue.delete(state.data[domain], key);
  }
};

const domainPropsActions: CommonActionTree<DomainPropsState> = {
  FETCH_DOMAIN_KEYS(_, domain: string): Promise<FetchResponse> {
    return this.$api.get(`/api/v2/domains/${domain}/props`)
      .then(({ data }) => data.map(({ key }) => key));
  },
  FETCH_CURRENT_DOMAIN_PROP({ dispatch }, { key }): Promise<any> {
    const authStore = useAuthStore();
    return dispatch('SAFE_FETCH_PROP', {
      domain: authStore.currentDomain,
      key
    });
  },
  SAFE_FETCH_PROP({ dispatch }, { key, domain }): Promise<any> {
    return dispatch('FETCH_DOMAIN_KEYS', domain)
      .then(keys => {
        if (keys.includes(key)) {
          return dispatch('FETCH_PROP', { key, domain });
        }
        return Promise.resolve();
      });
  },
  FETCH_PROP({ commit }, { key, domain }): Promise<FetchResponse> {
    return this.$api.get(`/api/v2/domains/${domain}/props/${key}`)
      .then(({ data: value }) => {
        commit('SET', { domain, key, value });
        return value;
      });
  },
  SET({ commit }, { key, value, domain, access = DOMAIN_PROPS_ACCESS_TYPES.AUTH }): Promise<FetchResponse> {
    return this.$api.put(`/api/v2/domains/${domain}/props/${key}`, value)
      .catch(err => {
        if (err instanceof NotFoundError) {
          return this.$api.post(`/api/v2/domains/${domain}/props/${key}`, { data: value, access });
        }
        throw err;
      })
      .then((res) => {
        commit('SET', { domain, key, value });
        return res;
      });
  },
  DELETE_PROP({ commit }, { key, domain }): Promise<FetchResponse> {
    return this.$api.delete(`/api/v2/domains/${domain}/props/${key}`)
      .then((res) => {
        commit('DELETE', { key, domain });
        return res;
      });
  }
};

export default new class implements CommonStore<DomainPropsState, unknown> {
  name = 'domainProps';
  state = {
    data: {}
  };

  getters = domainPropsGetters;
  mutations = domainPropsMutations;
  actions = domainPropsActions;
}();
